import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ConnectedDomain,
  ListConnectedDomainsOptions,
  ListConnectedDomainsResponse,
  createConnectedDomain as universalCreateConnectedDomain,
  deleteConnectedDomain as universalDeleteConnectedDomain,
  getConnectedDomain as universalGetConnectedDomain,
  listConnectedDomains as universalListConnectedDomains,
} from './premium-domains-v1-connected-domain-connected-domains.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/domains' };

export function createConnectedDomain(
  httpClient: HttpClient
): CreateConnectedDomainSignature {
  return (connectedDomain: NonNullablePaths<ConnectedDomain, `domain`, 2>) =>
    universalCreateConnectedDomain(
      connectedDomain,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateConnectedDomainSignature {
  /**
   * Creates a `connectedDomain` object and starts the domain connection process. It may take up to 48 hours for the DNS changes to take effect.
   *
   *  You must pass the relevant Wix account ID in the header of the call. You may also pass a Wix site ID in the header.
   *
   *  You can only connect external domains using this endpoint.
   *
   *  You can connect both root domains and subdomains to Wix sites.
   *
   *  The domain can be assigned as a primary domain or a redirect. You can read more about [primary and redirected domains](https://support.wix.com/en/article/switching-your-primary-and-redirected-domains). If you connect a domain as `"PRIMARY"` to a Wix site, the existing primary domain automatically becomes unassigned.
   *
   * > **Important:** This call requires an account level API key and cannot be authenticated with the standard authorization header.
   * @param - Domain to connect.
   * @returns Connected domain.
   */
  (connectedDomain: NonNullablePaths<ConnectedDomain, `domain`, 2>): Promise<
    NonNullablePaths<
      ConnectedDomain,
      | `_id`
      | `domain`
      | `connectionType`
      | `siteInfo.redirectInfo.primaryDomain`
      | `siteInfo.assignmentType`
      | `suppressNotifications`
      | `dnsPropagationStatus`,
      4
    >
  >;
}

export function getConnectedDomain(
  httpClient: HttpClient
): GetConnectedDomainSignature {
  return (connectedDomainId: string) =>
    universalGetConnectedDomain(
      connectedDomainId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetConnectedDomainSignature {
  /**
   * Retrieves a connected domain.
   *
   *
   * You must pass the relevant Wix account ID in the header of the call.
   *
   * > __Important:__ This call requires an account level API key and cannot be authenticated with the standard authorization header.
   * @param - ID of the connected domain. Identical to the domain name including TLD.
   * @returns Retrieved connected domain.
   */
  (connectedDomainId: string): Promise<
    NonNullablePaths<
      ConnectedDomain,
      | `_id`
      | `domain`
      | `connectionType`
      | `siteInfo.redirectInfo.primaryDomain`
      | `siteInfo.assignmentType`
      | `suppressNotifications`
      | `dnsPropagationStatus`,
      4
    >
  >;
}

export function deleteConnectedDomain(
  httpClient: HttpClient
): DeleteConnectedDomainSignature {
  return (connectedDomainId: string) =>
    universalDeleteConnectedDomain(
      connectedDomainId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteConnectedDomainSignature {
  /**
   * Deletes a `connectedDomain` object and removes related DNS records from
   * [Google's Cloud DNS](https://cloud.google.com/dns).
   *
   *
   * You must pass the relevant Wix account ID in the header of the call.
   *
   * If you delete a `"PRIMARY"` domain, the site automatically uses the standard
   * URL for free Wix sites. Read more about
   * [URLs of free Wix sites](https://support.wix.com/en/article/changing-your-free-wix-url-8591528).
   *
   * When you delete a subdomain, its CNAME record is deleted from Google's Cloud DNS.
   *
   * > __Important:__ This call requires an account level API key and cannot be authenticated with the standard authorization header.
   * @param - ID of the connected domain to delete. Identical to the domain name including TLD.
   */
  (connectedDomainId: string): Promise<void>;
}

export function listConnectedDomains(
  httpClient: HttpClient
): ListConnectedDomainsSignature {
  return (options?: ListConnectedDomainsOptions) =>
    universalListConnectedDomains(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListConnectedDomainsSignature {
  /**
   * Retrieves a list of up to 100 connected domains.
   *
   *
   * You must pass the relevant Wix account ID in the header of the call.
   *
   * The retrieved domains are sorted by `createdDate` in descending order.
   *
   * > __Important:__ This call requires an account level API key and cannot be authenticated with the standard authorization header.
   * @param - Filter options.
   */
  (options?: ListConnectedDomainsOptions): Promise<
    NonNullablePaths<
      ListConnectedDomainsResponse,
      | `connectedDomains`
      | `connectedDomains.${number}._id`
      | `connectedDomains.${number}.domain`
      | `connectedDomains.${number}.connectionType`
      | `connectedDomains.${number}.siteInfo.redirectInfo.primaryDomain`
      | `connectedDomains.${number}.siteInfo.assignmentType`
      | `connectedDomains.${number}.suppressNotifications`
      | `connectedDomains.${number}.dnsPropagationStatus`,
      6
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AssignmentType,
  ConnectedDomain,
  ConnectionType,
  CreateConnectedDomainRequest,
  CreateConnectedDomainResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeleteConnectedDomainRequest,
  DeleteConnectedDomainResponse,
  DnsPropagationStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  GetConnectedDomainRequest,
  GetConnectedDomainResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListConnectedDomainsOptions,
  ListConnectedDomainsRequest,
  ListConnectedDomainsResponse,
  MessageEnvelope,
  RedirectAssignmentInfo,
  RestoreInfo,
  SiteInfo,
  SiteInfoAssignmentInfoOneOf,
  WebhookIdentityType,
} from './premium-domains-v1-connected-domain-connected-domains.universal.js';
